import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchMembershipCatalog,
  fetchMyMembership,
  type MembershipCatalogSettings,
  type MembershipPlan,
} from './membershipService';
import {
  formatMembershipMoney,
  getGoldCompleteCopy,
  getNoMembershipPitch,
  getPlanDetailExtra,
  getUpgradeCtaLabel,
  getUpgradePitch,
  type BillingInterval,
  type HubPitchCopy,
} from './planDetails';
import {
  activateMembership,
  applyServerEnrollmentToLocal,
  readMembershipStatus,
} from './membershipStatus';
import {
  formatMembershipTierLabel,
  useClientMembership,
} from './useClientMembership';
import { MembershipPlanModal } from './MembershipPlanModal';
import './MembershipPricingPage.css';

const PlanIcon: React.FC<{ theme: string }> = ({ theme }) => {
  if (theme === 'gold') {
    return (
      <div className={`mp-icon mp-icon--${theme}`} aria-hidden>
        <svg viewBox="0 0 56 56" width="32" height="32" fill="none">
          <path
            d="M28 8l3.2 6.5 7.2 1-5.2 5.1 1.3 7.2L28 24.4l-6.5 3.4 1.3-7.2-5.2-5.1 7.2-1L28 8z"
            fill="currentColor"
            opacity="0.95"
          />
          <path
            d="M16 26c0 11.5 12 19.5 12 19.5S40 37.5 40 26"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <path
            d="M20 26h16"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            opacity="0.55"
          />
        </svg>
      </div>
    );
  }
  if (theme === 'silver') {
    return (
      <div className={`mp-icon mp-icon--${theme}`} aria-hidden>
        <svg viewBox="0 0 56 56" width="32" height="32" fill="none">
          <path
            d="M28 10l13 7.2v11.2c0 9.4-7.2 15.4-13 17.6-5.8-2.2-13-8.2-13-17.6V17.2L28 10z"
            fill="currentColor"
            opacity="0.18"
          />
          <path
            d="M28 12.5l11 6.1v10c0 8.2-6.3 13.5-11 15.5-4.7-2-11-7.3-11-15.5v-10l11-6.1z"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          <path
            d="M28 22v12M22 28h12"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }
  return (
    <div className={`mp-icon mp-icon--${theme}`} aria-hidden>
      <svg viewBox="0 0 56 56" width="32" height="32" fill="none">
        <path
          d="M28 10l13 7.2v11.2c0 9.4-7.2 15.4-13 17.6-5.8-2.2-13-8.2-13-17.6V17.2L28 10z"
          fill="currentColor"
          opacity="0.16"
        />
        <path
          d="M28 12.5l11 6.1v10c0 8.2-6.3 13.5-11 15.5-4.7-2-11-7.3-11-15.5v-10l11-6.1z"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinejoin="round"
        />
        <path
          d="M23.5 28.5l3.2 3.2 6.3-7"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

function perkLabels(plan: MembershipPlan): string[] {
  return [...(plan.perks || [])]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((p) => p.label);
}

function formatShortDate(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const MembershipPricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    clientId,
    membership,
    setMembership,
    isLive,
    isPending,
    isExpired,
  } = useClientMembership({ syncFromServer: false });

  const [billing, setBilling] = useState<BillingInterval>('monthly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [title, setTitle] = useState('Membership plans');
  const [subtitle, setSubtitle] = useState(
    'Choose the support level that fits your immigration journey.'
  );
  const [saveBadge, setSaveBadge] = useState('Save up to 29%');
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [hubSettings, setHubSettings] = useState<MembershipCatalogSettings | null>(
    null
  );
  const [selected, setSelected] = useState<MembershipPlan | null>(null);
  const [showCongrats, setShowCongrats] = useState(false);
  const [congratsPlan, setCongratsPlan] = useState<MembershipPlan | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMembershipCatalog();
        if (cancelled) return;
        setEnabled(data.enabled !== false);
        if (data.settings) {
          setTitle(data.settings.page_title || title);
          setSubtitle(data.settings.page_subtitle || subtitle);
          setSaveBadge(data.settings.annual_save_badge || saveBadge);
          setHubSettings(data.settings);
        }
        setPlans(data.plans || []);

        if (clientId) {
          try {
            const mine = await fetchMyMembership();
            if (cancelled) return;
            const enroll = mine.enrollment;
            if (enroll && mine.status !== 'none') {
              const planMeta = (data.plans || []).find(
                (p) => p.plan_id === enroll.plan_id
              );
              applyServerEnrollmentToLocal(
                clientId,
                { status: mine.status, enrollment: enroll },
                planMeta
                  ? {
                      plan_name: planMeta.name,
                      name: planMeta.name,
                      subtitle: planMeta.subtitle,
                      plan_subtitle: planMeta.subtitle,
                      service_discount_percent: planMeta.service_discount_percent,
                      perk_labels: perkLabels(planMeta),
                    }
                  : undefined
              );
              setMembership(readMembershipStatus(clientId));
            }
          } catch {
            /* offline — keep local */
          }
        }
      } catch {
        if (!cancelled) setError('Unable to load membership plans right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (loading || plans.length === 0) return;
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const planId = searchParams.get('plan') || '';
    const billingParam =
      searchParams.get('billing') === 'annual' ? 'annual' : 'monthly';

    if (payment !== 'success' || !sessionId || !planId || !clientId) return;

    const plan = plans.find((p) => p.plan_id === planId);
    if (!plan) return;

    activateMembership(clientId, {
      plan_id: plan.plan_id,
      plan_name: plan.name,
      plan_subtitle: plan.subtitle,
      tier: plan.tier,
      billing: billingParam,
      service_discount_percent: plan.service_discount_percent,
      perk_labels: perkLabels(plan),
    });
    setBilling(billingParam);
    setCongratsPlan(plan);
    setShowCongrats(true);
    setMembership(readMembershipStatus(clientId));
    setSearchParams({}, { replace: true });

    void (async () => {
      for (let i = 0; i < 5; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const mine = await fetchMyMembership();
          if (
            mine.enrollment &&
            (mine.status === 'active' || mine.status === 'past_due')
          ) {
            applyServerEnrollmentToLocal(
              clientId,
              { status: mine.status, enrollment: mine.enrollment },
              {
                plan_name: plan.name,
                name: plan.name,
                subtitle: plan.subtitle,
                plan_subtitle: plan.subtitle,
                service_discount_percent: plan.service_discount_percent,
                perk_labels: perkLabels(plan),
              }
            );
            setMembership(readMembershipStatus(clientId));
            break;
          }
        } catch {
          /* retry */
        }
      }
    })();
  }, [loading, plans, searchParams, setSearchParams, clientId, setMembership]);

  const ordered = useMemo(() => {
    const order = { bronze: 1, silver: 2, gold: 3 } as const;
    return [...plans].sort((a, b) => order[a.tier] - order[b.tier]);
  }, [plans]);

  const currentTier = isLive || isPending ? membership.tier : undefined;
  const showCatalog = !isLive && !isPending;

  const pitch: HubPitchCopy = useMemo(() => {
    if (isLive && currentTier === 'gold') {
      return getGoldCompleteCopy({
        title: hubSettings?.hub_gold_title,
        body: hubSettings?.hub_gold_body,
      });
    }
    if (isLive && (currentTier === 'bronze' || currentTier === 'silver')) {
      return getUpgradePitch(
        currentTier,
        currentTier === 'bronze'
          ? {
              title: hubSettings?.hub_bronze_upgrade_title,
              body: hubSettings?.hub_bronze_upgrade_body,
              bullets: hubSettings?.hub_bronze_upgrade_bullets,
            }
          : {
              title: hubSettings?.hub_silver_upgrade_title,
              body: hubSettings?.hub_silver_upgrade_body,
              bullets: hubSettings?.hub_silver_upgrade_bullets,
            }
      );
    }
    return getNoMembershipPitch({
      title: hubSettings?.hub_none_title,
      body: hubSettings?.hub_none_body,
      bullets: hubSettings?.hub_none_bullets,
    });
  }, [isLive, currentTier, hubSettings]);

  const currentPlan = useMemo(() => {
    if (!membership.plan_id) return null;
    return ordered.find((p) => p.plan_id === membership.plan_id) || null;
  }, [ordered, membership.plan_id]);

  const currentBenefits = useMemo(() => {
    if (membership.perk_labels?.length) return membership.perk_labels.slice(0, 8);
    if (currentPlan) return perkLabels(currentPlan).slice(0, 8);
    if (membership.tier) return getPlanDetailExtra(membership.tier).highlights;
    return [];
  }, [membership, currentPlan]);

  const goGetStarted = (plan: MembershipPlan) => {
    setSelected(null);
    navigate(
      `/membership/get-started?plan=${encodeURIComponent(plan.plan_id)}&billing=${billing}`
    );
  };

  const activatedLabel = formatShortDate(membership.activated_at);
  const renewLabel = formatShortDate(membership.expires_at);
  const upgradeCta = getUpgradeCtaLabel(currentTier);
  const tierLabel = formatMembershipTierLabel(membership.tier);

  if (loading) {
    return (
      <div className="mp-page">
        <div className="mp-atmosphere" aria-hidden />
        <div className="mp-loading">
          Loading membership
          <div className="mp-loading-dots" aria-hidden>
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="mp-page is-ready">
        <div className="mp-atmosphere" aria-hidden />
        <div className="mp-shell">
          <div className="mp-empty">
            <h1>Membership</h1>
            <p>Plans are not available right now. Please check back soon.</p>
            <button type="button" className="mp-text-link" onClick={() => navigate('/')}>
              Return home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`mp-page is-ready${isLive ? ` mp-page--member mp-page--${membership.tier || 'bronze'}` : ''}`}
    >
      <div className="mp-atmosphere" aria-hidden />
      <div className="mp-shell">
        {showCongrats && congratsPlan && (
          <div className="mp-congrats" role="status">
            <div className="mp-congrats-icon" aria-hidden>
              <svg viewBox="0 0 64 64" width="56" height="56" fill="none">
                <circle cx="32" cy="32" r="30" fill="#E8F5E9" />
                <path
                  d="M18 33.5l9 9 19-20"
                  stroke="#2E7D32"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="mp-congrats-title">Congratulations!</h2>
            <p className="mp-congrats-body">
              You’re on <strong>{congratsPlan.name}</strong>
              {renewLabel ? ` · renews ${renewLabel}` : ''}.
              {membership.billing
                ? ` Billed ${membership.billing === 'annual' ? 'annually' : 'monthly'}.`
                : ''}
            </p>
            <p className="mp-congrats-sub">
              Your member benefits are ready on this page.
            </p>
            <button
              type="button"
              className="mp-congrats-dismiss"
              onClick={() => setShowCongrats(false)}
            >
              Continue
            </button>
          </div>
        )}

        <header className="mp-header">
          <h1 className="mp-title">{isLive ? 'Your membership' : title}</h1>
          {!isLive && !isPending && <p className="mp-subtitle">{subtitle}</p>}
          {isLive && renewLabel && (
            <p className="mp-subtitle">
              {tierLabel} member · renews {renewLabel}
            </p>
          )}
        </header>

        {isExpired && (
          <section className="mp-status mp-status--expired" aria-live="polite">
            <div className="mp-status-top">
              <div>
                <div className="mp-status-chips">
                  <span className="mp-chip is-pending">Expired</span>
                  {membership.tier && (
                    <span className="mp-chip is-tier">{membership.tier}</span>
                  )}
                </div>
                <h2 className="mp-status-name">
                  {membership.plan_name || 'Membership'} ended
                </h2>
                <p className="mp-status-meta">
                  Your plan is no longer active
                  {membership.expires_at
                    ? ` (ended ${formatShortDate(membership.expires_at)})`
                    : ''}
                  .
                </p>
              </div>
              <button
                type="button"
                className="mp-cta mp-cta--silver mp-upgrade-cta"
                onClick={() => navigate('/membership/upgrade?mode=renew')}
              >
                Renew membership
              </button>
            </div>
          </section>
        )}

        {(isLive || isPending) && (
          <section
            className={`mp-status mp-status--member mp-status--${membership.tier || 'bronze'}`}
          >
            <div className="mp-status-top">
              <div>
                <div className="mp-status-chips">
                  <span className={`mp-chip ${isLive ? 'is-live' : 'is-pending'}`}>
                    {isLive ? 'Live' : 'Payment pending'}
                  </span>
                  {membership.tier && (
                    <span className="mp-chip is-tier">{membership.tier}</span>
                  )}
                </div>
                <h2 className="mp-status-name">
                  {membership.plan_name || currentPlan?.name || 'Membership'}
                </h2>
                <p className="mp-status-meta">
                  {[
                    membership.plan_subtitle || currentPlan?.subtitle,
                    membership.billing === 'annual'
                      ? 'Annual billing'
                      : 'Monthly billing',
                    membership.service_discount_percent
                      ? `${membership.service_discount_percent}% off services`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              {isPending && membership.plan_id && (
                <button
                  type="button"
                  className="mp-cta mp-cta--silver"
                  onClick={() =>
                    navigate(
                      `/membership/get-started?plan=${encodeURIComponent(membership.plan_id!)}&billing=${membership.billing || 'monthly'}`
                    )
                  }
                >
                  Continue payment
                </button>
              )}
            </div>

            {isLive && (activatedLabel || renewLabel) && (
              <dl className="mp-member-facts">
                {activatedLabel && (
                  <div>
                    <dt>Started</dt>
                    <dd>{activatedLabel}</dd>
                  </div>
                )}
                {renewLabel && (
                  <div>
                    <dt>Renews / ends</dt>
                    <dd>{renewLabel}</dd>
                  </div>
                )}
                {membership.billing && (
                  <div>
                    <dt>Billing</dt>
                    <dd>
                      {membership.billing === 'annual' ? 'Annual' : 'Monthly'}
                    </dd>
                  </div>
                )}
              </dl>
            )}

            {currentBenefits.length > 0 && (
              <ul className="mp-status-perks">
                {currentBenefits.map((b) => (
                  <li key={b}>
                    <span aria-hidden>✓</span>
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Live members: upgrade strip only when a higher tier exists */}
        {isLive && upgradeCta && (
          <section
            className={`mp-upgrade-strip mp-upgrade-strip--${
              currentTier === 'bronze' ? 'silver' : 'gold'
            }`}
          >
            <div className="mp-upgrade-strip-copy">
              <h2 className="mp-pitch-title">{pitch.title}</h2>
              <p className="mp-pitch-body">{pitch.body}</p>
            </div>
            <button
              type="button"
              className={`mp-cta mp-cta--${
                currentTier === 'bronze' ? 'silver' : 'gold'
              } mp-upgrade-cta`}
              onClick={() => navigate('/membership/upgrade')}
            >
              {upgradeCta}
              <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
                <path
                  d="M4 12L12 4M12 4H6.5M12 4V9.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </section>
        )}

        {/* Non-members / expired: catalog browse (expired renew uses upgrade page primarily) */}
        {showCatalog && (
          <>
            <section className="mp-pitch">
              <h2 className="mp-pitch-title">{pitch.title}</h2>
              <p className="mp-pitch-body">{pitch.body}</p>
              {pitch.bullets.length > 0 && (
                <ul className="mp-pitch-list">
                  {pitch.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              )}
            </section>

            <div className="mp-section-head">
              <h2 className="mp-section-title">{title}</h2>
              <p className="mp-section-sub">{subtitle}</p>
              <div className="mp-billing-wrap">
                <div className="mp-billing" role="group" aria-label="Billing interval">
                  <button
                    type="button"
                    className={`mp-billing-btn${billing === 'monthly' ? ' is-active' : ''}`}
                    onClick={() => setBilling('monthly')}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    className={`mp-billing-btn${billing === 'annual' ? ' is-active' : ''}`}
                    onClick={() => setBilling('annual')}
                  >
                    Annual
                  </button>
                </div>
                <span className="mp-save-badge">{saveBadge}</span>
              </div>
            </div>

            {error && <div className="mp-error">{error}</div>}

            <div className="mp-grid">
              {ordered.map((plan, index) => {
                const price =
                  billing === 'monthly' ? plan.monthly_amount : plan.annual_amount;
                const periodLabel = billing === 'monthly' ? '/mo' : '/yr';
                const billedHint =
                  billing === 'monthly'
                    ? 'Billed monthly'
                    : `Billed annually · ~${formatMembershipMoney(plan.annual_amount / 12, plan.currency)}/mo`;

                return (
                  <article
                    key={plan.plan_id}
                    className={`mp-card mp-card--${plan.theme}${plan.featured ? ' is-featured' : ''}`}
                    style={{ animationDelay: `${120 + index * 90}ms` }}
                    onClick={() => setSelected(plan)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelected(plan);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${plan.name} plan details`}
                  >
                    {plan.featured && <div className="mp-popular">Most popular</div>}
                    <PlanIcon theme={plan.theme} />
                    <div className="mp-card-top">
                      <h2 className="mp-plan-name">{plan.name}</h2>
                      <p className="mp-plan-sub">{plan.subtitle}</p>
                    </div>
                    <div className="mp-price-block">
                      <div className="mp-price-row">
                        <span
                          key={`${plan.plan_id}-${billing}`}
                          className="mp-price"
                        >
                          {formatMembershipMoney(price, plan.currency)}
                        </span>
                        <span className="mp-period">{periodLabel}</span>
                      </div>
                      <p className="mp-billed">{billedHint}</p>
                    </div>
                    {plan.includes_previous_tier && plan.previous_tier_banner ? (
                      <div className="mp-banner">{plan.previous_tier_banner}</div>
                    ) : (
                      <div className="mp-banner mp-banner--muted">What’s included</div>
                    )}
                    <ul className="mp-perks">
                      {[...plan.perks]
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((perk) => (
                          <li key={perk.perk_id}>
                            <span className="mp-check" aria-hidden>
                              <svg viewBox="0 0 16 16" width="12" height="12">
                                <path
                                  d="M3.5 8.2l2.8 2.8 6.2-6.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <span>{perk.label}</span>
                          </li>
                        ))}
                    </ul>
                    <p className="mp-card-hint">Tap for full details</p>
                    <button
                      type="button"
                      className={`mp-cta mp-cta--${plan.theme}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        goGetStarted(plan);
                      }}
                    >
                      {plan.cta_label || 'Get started'}
                      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
                        <path
                          d="M4 12L12 4M12 4H6.5M12 4V9.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </article>
                );
              })}
            </div>
          </>
        )}

        <p className="mp-footnote">
          Cancel anytime · Member discounts apply automatically on eligible service orders
        </p>
      </div>

      {selected && (
        <MembershipPlanModal
          plan={selected}
          billing={billing}
          onClose={() => setSelected(null)}
          onGetStarted={goGetStarted}
        />
      )}
    </div>
  );
};

export default MembershipPricingPage;
